import {
  getRelativePosition,
  useGetCollector,
  useUpdateCollector,
} from "@/api/collector/collector";
import { Collector } from "@/lib/types";
import { useForm } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";

import * as AF from "@/components/form-items/AbstractForm";
import { Separator } from "@/components/ui/separator";
import { FormLayout } from "@/layout/FormLayout";
import LinksManagement from "@/components/LinksManagement";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { useQueryClient } from "@tanstack/react-query";
import { collectorSchema } from "@/api/collector/collector-validation";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";

const schema = collectorSchema.passthrough();

const EditCollector = ({ collector }: { collector: Collector }) => {
  const form = useForm({
    reValidateMode: "onSubmit",
    resolver: standardSchemaResolver(schema),
    defaultValues: collector,
  });

  const collectorUpdater = useUpdateCollector();

  const { relativePosition } = useDashboardParams<Collector>({
    statusField: "collectionneursPublicationStatus",
    resourceName: "bibliography",
    relativePosition: {
      currentId: collector.id,
      positionFetcher: getRelativePosition,
    },
  });

  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const [, setSearchParam] = useSearchParams();

  return (
    <FormLayout
      publicationStatus={collector.collectionneursPublicationStatus}
      onSave={(status) => {
        form.handleSubmit(
          (values) => {
            collectorUpdater.mutate(
              {
                ...values,
                ...(status && { collectionneursPublicationStatus: status }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t(
                      status === undefined
                        ? "autoSave"
                        : "recordSavedToastTitle",
                    ),
                    variant: "success",
                    description: t("collectorSavedToastDescription"),
                  });
                  form.reset({}, { keepValues: true });
                },
              },
            );
          },
          (invalid) => {
            if (invalid.collectionneursDescriptionNom != undefined)
              setSearchParam({ tab: "description" });
          },
        )();
      }}
      invalidateOnChange={() => {
        queryClient.invalidateQueries({
          queryKey: ["collectors"],
        });
        queryClient.invalidateQueries({
          queryKey: ["collector", collector.id],
        });
        queryClient.invalidateQueries({
          queryKey: ["attachment", { docId: collector.id }],
        });
      }}
      relativePosition={relativePosition}
      title={`${collector.collectionneursDescriptionCivilite} ${collector.collectionneursDescriptionPrenom} ${collector.collectionneursDescriptionNom}`}
      reference={`${collector.collectionneursDescriptionVille ?? ""} ${collector.collectionneursDescriptionVille && collector.collectionneursDescriptionPays ? "-" : ""} ${collector.collectionneursDescriptionPays ?? ""}`}
      id={collector.id}
      form={form}
      type="collector"
    >
      <Separator />
      <AF.AbstractForm
        form={form}
        tabs={
          [
            {
              value: "description",
              label: t("collectorDescription"),
            },
            { value: "sources", label: t("sources") },
            { value: "links", label: t("linkedEntries") },
          ] as const
        }
      >
        <AF.Tab value="description">
          <AF.Col>
            <ModifiableList
              className="w-32"
              name="collectionneursDescriptionCivilite"
              listKey="liste-civilites"
            />
            <AF.Text name="collectionneursDescriptionPrenom" />
            <AF.Text name="collectionneursDescriptionNom" />
            <AF.Text name="collectionneursDescriptionNomTri" />
            <AF.Text name="collectionneursDescriptionLieu" />
            <AF.Text name="collectionneursDescriptionRue" />
            <AF.Text name="collectionneursDescriptionCodePostal" />
            <AF.Row>
              <ModifiableList
                name="collectionneursDescriptionVille"
                listKey="liste-villes"
              />
              <ModifiableList
                name="collectionneursDescriptionPays"
                listKey="liste-pays"
              />
            </AF.Row>
            <AF.Checkbox
              name="collectionneursDescriptionCollectionPublique"
              align={true}
            />
          </AF.Col>
          <AF.Col>
            <AF.Text name="collectionneursDescriptionNomContact" />
            <AF.Row>
              <AF.Text name="collectionneursDescriptionTelephoneMobile" />
              <AF.Text name="collectionneursDescriptionTelephone" />
            </AF.Row>
            <AF.Text name="collectionneursDescriptionAdresseMail" />
            <AF.Text name="collectionneursDescriptionSiteWeb" />
            <AF.RichText name="collectionneursDescriptionNotes" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="sources">
          <AF.RichText name="sources" />
        </AF.Tab>
        <AF.Tab value="links">
          <LinksManagement
            type="collector"
            id={collector.id}
            data={collector}
            configuration={{
              fields: [
                {
                  name: "links",
                  type: "links",
                  label: "Liens",
                  links: [
                    {
                      name: "relatedArtwork",
                      label: t("entryartworks"),
                      type: "link-simple",
                    },
                    {
                      name: "collectorInMultipleprints",
                      label: t("entrymultipleprints"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "estampesDansCollectionneursDescriptionInfos",
                          type: "text",
                          label: t(
                            "estampesDansCollectionneursDescriptionInfos",
                          ),
                          className: "w-full",
                        },
                      ],
                    },
                  ],
                },
              ],
            }}
          />
        </AF.Tab>
      </AF.AbstractForm>
    </FormLayout>
  );
};

export const EditCollectorPage = () => {
  const { id } = useParams();
  const { data, isLoading } = useGetCollector(id);
  const { t } = useTranslation();
  return (
    <>
      {data && <EditCollector key={data.id} collector={data} />}
      {!data && !isLoading && (
        <h2 className="p-4">{t("collectionneurNExistePasLabel")}</h2>
      )}
    </>
  );
};
